/* Live formula diagnostics (excel-formula-editor-proposal P2): the save-time lint surfaced at
   keystroke time, plus the two advisory checks. Faithful JS ports of the C# authorities —
   ExcelFunctions.FindUnknownFunctionNames / TranslateDateFormat,
   NativeFormulaEvaluator.ProtectStringLiterals and the name rules of ExcelFormulaSyntax (how
   field formulas are read before that lint runs) — pinned by the SHARED vector file
   (tests/unit/design/formula-lint-vectors.json, twin copy in FETIAS.Business.Tests/Automation).
   Pure logic only; FTSFormula.vue owns the strip UI.

   Severity contract: "error" = the save WILL bounce (mirror of CanvasConsistencyChecker);
   "warn" = legal but costs the native lane. Worst first. */

const CHIP_REGEX = /{{.*?}}/g;
const UPPERCASE_CALL_REGEX = /\b([A-Z][A-Z0-9_]+)\s*\(/g;

/* Port of NativeFormulaEvaluator.ProtectStringLiterals: replaces every '...' / "..." literal
   with an inert placeholder (handles \' \" \\ escapes; an unbalanced quote leaves the tail
   untouched rather than guessing). The placeholder carries no letters, so the scans that run
   over the protected text can never match inside a literal. */
export const protectStringLiterals = formula => {
  let out = "";
  for (let i = 0; i < formula.length; i++) {
    const c = formula[i];
    if (c !== "'" && c !== '"') {
      out += c;
      continue;
    }
    const quote = c;
    let content = "";
    let closed = false;
    for (i = i + 1; i < formula.length; i++) {
      if (formula[i] === "\\" && i + 1 < formula.length
        && (formula[i + 1] === quote || formula[i + 1] === "\\")) {
        content += formula[i + 1];
        i++;
        continue;
      }
      if (formula[i] === quote) {
        closed = true;
        break;
      }
      content += formula[i];
    }
    if (!closed) {
      // unbalanced quote — leave the tail untouched rather than guessing
      out += quote + content;
      break;
    }
    out += "\u00060\u0006";
  }
  return out;
};

/* Port of ExcelFunctions.FindUnknownFunctionNames: an ALL-UPPERCASE identifier (2+ chars)
   followed by "(" that is not in the registry reads as an intended spreadsheet function.
   Chips and string literals are stripped first; lowercase/mixed-case calls are presumed
   Python-lane vocabulary. `knownNames` comes from the fetched registry (ExcelFunctionCatalog
   membership = KnownFunctionNames, pinned server-side). */
export const findUnknownFunctionNames = (rawFormula, knownNames) => {
  const unknown = [];
  if (!rawFormula || !rawFormula.trim()) return unknown;

  let text = rawFormula.replace(CHIP_REGEX, " ");
  text = protectStringLiterals(text);

  let match;
  UPPERCASE_CALL_REGEX.lastIndex = 0;
  while ((match = UPPERCASE_CALL_REGEX.exec(text)) !== null) {
    const name = match[1];
    if (!knownNames.has(name) && unknown.indexOf(name) < 0) unknown.push(name);
  }
  return unknown;
};

/* ---- the Excel spellings (ExcelFormulaSyntax) ----------------------------------------------- */

/* Field and column formulas are read with the Excel spellings an author (or an AI) writes from
   spreadsheet habit (ExcelFormulaSyntax.NormalizeStored, gap-closure 2026-10-18): SUM( is the
   table total sum(, TRUE / FALSE are the constants, and a leading "=" marks the recommended
   syntax. The save-time lint reads the formula that way before it checks function names
   (CanvasConsistencyChecker.CheckFieldFormulas). Automation block expressions are NOT read that
   way — SUM there is still a NameError — so the strict scan above stays their lint. */
const EXCEL_TRUE_FALSE_REGEX = /\b(TRUE|FALSE)\b/g;
const EXCEL_SUM_REGEX = /\bSUM(?=\s*\()/g;
const PLACEHOLDER_REGEX = /__x[st](\d+)__/g;
const WORD_REGEX = /[A-Za-z_]\w*/g;
const UPPERCASE_NAME_REGEX = /^[A-Z][A-Z0-9_]+$/;

/* Port of ExcelFormulaSyntax.Mask (legacy reading) and MaskRecommended (excelQuotes): {{tokens}}
   and text literals become placeholders. Legacy literals take a backslash escape; Excel literals
   double the quote instead. Null when a literal or a token is left open — the server then reads
   the formula as written. */
const maskExcelSyntax = (text, store, excelQuotes) => {
  let out = "";
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === "{" && text[i + 1] === "{") {
      const end = text.indexOf("}}", i + 2);
      if (end < 0) return null;
      out += `__xt${store.length}__`;
      store.push(text.slice(i, end + 2));
      i = end + 1;
      continue;
    }
    if (c === "'" || c === '"') {
      let j = i + 1;
      let closed = false;
      for (; j < text.length; j++) {
        if (!excelQuotes && text[j] === "\\") { j++; continue; }
        if (text[j] === c) {
          if (excelQuotes && text[j + 1] === c) { j++; continue; }
          closed = true;
          break;
        }
      }
      if (!closed) return null;
      out += `__xs${store.length}__`;
      store.push(text.slice(i, j + 1));
      i = j;
      continue;
    }
    out += c;
  }
  return out;
};

/* Port of ExcelFormulaSyntax.IsRecommended: the first non-blank character is a single "=". */
export const isRecommendedSyntax = formula => {
  const trimmed = (formula || "").trimStart();
  return trimmed.startsWith("=") && !trimmed.startsWith("==");
};

/* The NAME slice of ExcelFormulaSyntax.NormalizeStored (legacy reading): on the backend (first)
   line, TRUE / FALSE become True / False and SUM( becomes sum(; the rest is kept as stored. The
   other rewrites ("=", "<>", "&" → CONCAT) introduce no unknown name, so they are not ported. */
export const readExcelNames = storedFormula => {
  if (!storedFormula || !storedFormula.trim() || isRecommendedSyntax(storedFormula)) return storedFormula;
  const newline = storedFormula.indexOf("\n");
  const backendEnd = newline < 0 ? storedFormula.length
    : newline > 0 && storedFormula[newline - 1] === "\r" ? newline - 1 : newline;
  const backend = storedFormula.slice(0, backendEnd);

  const store = [];
  const masked = maskExcelSyntax(backend, store, false);
  if (masked === null) return storedFormula; // an unbalanced quote or token: read as written
  const code = masked
    .replace(EXCEL_TRUE_FALSE_REGEX, m => (m === "TRUE" ? "True" : "False"))
    .replace(EXCEL_SUM_REGEX, "sum");
  if (code === masked) return storedFormula;
  return code.replace(PLACEHOLDER_REGEX, (m, index) => store[Number(index)])
    + storedFormula.slice(backendEnd);
};

/* Port of the unknown-function verdicts of ExcelFormulaSyntax.ValidateRecommended, UPPERCASE
   calls only (the keystroke lint does not port the rest of that lint yet): every word not after
   a letter, digit or "." counts once; a call is unknown unless its canonical name is in the
   registry or is SUM. TRUE( / FALSE( are unknown here — the recommended syntax has no such
   functions. */
const findUnknownRecommendedNames = (firstLine, knownNames) => {
  const unknown = [];
  if (!isRecommendedSyntax(firstLine)) return unknown;
  const body = firstLine.trimStart().slice(1);
  if (!body.trim()) return unknown;
  const code = maskExcelSyntax(body, [], true);
  if (code === null) return unknown; // the server reports the open literal instead

  const reported = new Set();
  let match;
  WORD_REGEX.lastIndex = 0;
  while ((match = WORD_REGEX.exec(code)) !== null) {
    const word = match[0];
    const before = match.index > 0 ? code[match.index - 1] : "";
    if (/[\w.]/.test(before) || word.startsWith("__x") || reported.has(word)) continue;
    reported.add(word);
    if (!/^\s*\(/.test(code.slice(match.index + word.length))) continue;
    const upper = word.toUpperCase();
    if (upper === "SUM" || knownNames.has(upper)) continue;
    if (UPPERCASE_NAME_REGEX.test(word)) unknown.push(word);
  }
  return unknown;
};

/* The save-time lint's unknown-function verdict for a FIELD or COLUMN formula (value, visible,
   read-only, required, validate): the formula as the server reads it — the recommended syntax
   by its own lint, a legacy formula after the Excel spellings. */
export const findUnknownFieldFunctionNames = (storedFormula, knownNames) => {
  if (!storedFormula || !storedFormula.trim()) return [];
  if (isRecommendedSyntax(storedFormula))
    return findUnknownRecommendedNames(storedFormula.split(/\r?\n/)[0], knownNames);
  return findUnknownFunctionNames(readExcelNames(storedFormula), knownNames);
};

/* Port of ExcelFunctions.TranslateDateFormat, verdict-only: null when the pattern is a valid
   Excel date format, else the C# error message. Same token walk — AM/PM (any case), letter
   runs over {y m d h s}, the separator set "-/:., ", m/mm = minutes when the previous code
   run is hours OR the next is seconds, hour/second/minute runs capped at 2. */
export const validateDateFormat = pattern => {
  const tokens = [];
  for (let i = 0; i < pattern.length;) {
    if (i + 5 <= pattern.length && pattern.substr(i, 5).toUpperCase() === "AM/PM") {
      tokens.push({ isAmPm: true });
      i += 5;
      continue;
    }
    const c = pattern[i].toLowerCase();
    if (c === "y" || c === "m" || c === "d" || c === "h" || c === "s") {
      const start = i;
      while (i < pattern.length && pattern[i].toLowerCase() === c) i++;
      tokens.push({ letter: c, length: i - start });
      continue;
    }
    if ("-/:., ".indexOf(pattern[i]) >= 0) {
      while (i < pattern.length && "-/:., ".indexOf(pattern[i]) >= 0) i++;
      tokens.push({ separator: true });
      continue;
    }
    return `TEXT: unsupported format code '${pattern[i]}' in '${pattern}'`;
  }

  const letters = tokens.filter(t => t.letter);
  for (const token of letters) {
    if (token.letter === "h" && token.length > 2)
      return `TEXT: unsupported hour run in '${pattern}'`;
    if (token.letter === "s" && token.length > 2)
      return `TEXT: unsupported second run in '${pattern}'`;
    if (token.letter === "m") {
      const position = letters.indexOf(token);
      const previous = position > 0 ? letters[position - 1] : null;
      const next = position < letters.length - 1 ? letters[position + 1] : null;
      const minutes = (previous && previous.letter === "h") || (next && next.letter === "s");
      if (minutes && token.length > 2)
        return `TEXT: unsupported minute run in '${pattern}'`;
    }
  }
  return null;
};

/* The TEXT numeric-format charset (ExcelFunctions.Text) — anything else throws at run time. */
const NUMBER_FORMAT_CHARS = "0#.,% ";

const hasDateLetters = pattern =>
  /[a-z]/i.test(pattern.replace(/AM\/PM/gi, ""));

export const validateTextFormat = pattern => {
  if (hasDateLetters(pattern)) return validateDateFormat(pattern);
  for (const c of pattern) {
    if (NUMBER_FORMAT_CHARS.indexOf(c) < 0)
      return `TEXT: unsupported format code '${c}' in '${pattern}'`;
  }
  return null;
};

/* Levenshtein distance, for nearest-name suggestions on unknown functions. */
const distance = (a, b) => {
  const m = a.length, n = b.length;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(
        prev[j] + 1,
        cur[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    prev = cur;
  }
  return prev[n];
};

export const nearestName = (name, knownNames) => {
  let best = null;
  let bestScore = Infinity;
  for (const candidate of knownNames) {
    // a prefix relationship is a longhand/shorthand pair, not a typo — the real-Excel
    // spellings land here (CONCATENATE → CONCAT, ISNUMBER → nothing but ISBLANK stays far)
    if (name.indexOf(candidate) === 0 || candidate.indexOf(name) === 0) return candidate;
    const score = distance(name, candidate);
    if (score < bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  // otherwise only suggest when it plausibly IS a typo of the candidate
  return bestScore <= Math.max(2, Math.floor(name.length / 3)) ? best : null;
};

/* Scans the protected, chip-stripped text for calls of `name` and returns each call's
   top-level argument count (nesting-aware; stops at the matching close paren). */
const argCounts = (text, name) => {
  const counts = [];
  const callRegex = new RegExp("\\b" + name + "\\s*\\(", "g");
  let match;
  while ((match = callRegex.exec(text)) !== null) {
    let depth = 1;
    let args = 1;
    let sawContent = false;
    for (let i = match.index + match[0].length; i < text.length && depth > 0; i++) {
      const c = text[i];
      if (c === "(") depth++;
      else if (c === ")") depth--;
      else if (c === "," && depth === 1) args++;
      else if (depth >= 1 && c.trim()) sawContent = true;
    }
    counts.push(sawContent ? args : 0);
  }
  return counts;
};

/* The P2 diagnostic pass. `registry` is the fetched function list (name/signature/nativeArity).
   `excelSyntax`: the formula is a field or column formula, which the server reads with the Excel
   spellings (SUM, TRUE / FALSE, a leading "="); leave it off for automation block expressions.
   Returns [{ severity: "error"|"warn", message, name, suggestion? }], errors first. */
export const diagnose = (rawFormula, registry, { excelSyntax = false } = {}) => {
  if (!rawFormula || !rawFormula.trim() || !registry || !registry.length) return [];

  const knownNames = new Set(registry.map(f => f.name));
  const diagnostics = [];

  const unknownNames = excelSyntax
    ? findUnknownFieldFunctionNames(rawFormula, knownNames)
    : findUnknownFunctionNames(rawFormula, knownNames);
  for (const name of unknownNames) {
    const suggestion = nearestName(name, knownNames);
    diagnostics.push({
      severity: "error",
      name,
      suggestion,
      message: `Unknown function ${name}(…) — save will be rejected.`
    });
  }

  const protectedText = protectStringLiterals(rawFormula.replace(CHIP_REGEX, " "));

  for (const fn of registry) {
    if (!fn.nativeArity) continue;
    for (const count of argCounts(protectedText, fn.name)) {
      if (count > fn.nativeArity) {
        diagnostics.push({
          severity: "warn",
          name: fn.name,
          message: `${fn.name} with ${count} arguments runs on the server script lane — native supports ${fn.nativeArity}. Nest to stay native.`
        });
        break; // one hint per function is enough
      }
    }
  }

  // TEXT format literals: validate each TEXT(..., '...') second argument that is a literal.
  const textFormats = extractTextFormatLiterals(rawFormula.replace(CHIP_REGEX, " "));
  for (const format of textFormats) {
    const problem = validateTextFormat(format);
    if (problem) {
      diagnostics.push({
        severity: "warn",
        name: "TEXT",
        message: `${problem} — fails when this formula runs.`
      });
      break;
    }
  }

  return diagnostics.sort((a, b) =>
    (a.severity === "error" ? 0 : 1) - (b.severity === "error" ? 0 : 1));
};

/* Finds TEXT( calls whose LAST top-level argument is a string literal, and returns those
   literals (unescaped). Non-literal formats (a field, a chip) are runtime-only — skipped. */
export const extractTextFormatLiterals = text => {
  const formats = [];
  const callRegex = /\bTEXT\s*\(/g;
  let match;
  while ((match = callRegex.exec(text)) !== null) {
    let depth = 1;
    let lastArgStart = match.index + match[0].length;
    let end = -1;
    for (let i = lastArgStart; i < text.length && depth > 0; i++) {
      const c = text[i];
      if (c === "(") depth++;
      else if (c === ")") {
        depth--;
        if (depth === 0) end = i;
      } else if (c === "," && depth === 1) lastArgStart = i + 1;
    }
    if (end < 0) continue; // unclosed call — still typing
    const lastArg = text.slice(lastArgStart, end).trim();
    const literal = /^(['"])([\s\S]*)\1$/.exec(lastArg);
    if (literal) formats.push(literal[2].replace(/\\(['"\\])/g, "$1"));
  }
  return formats;
};

/* ---- signature help ------------------------------------------------------------------------ */

/* Given the CHIP-STRIPPED text before the caret, finds the innermost registry call the caret
   is inside and which parameter it is on. Returns { fn, paramIndex } or null. Comma counting
   runs over literal-protected text, so commas inside strings never advance the parameter. */
export const signatureAt = (textBeforeCaret, registry) => {
  if (!textBeforeCaret || !registry || !registry.length) return null;
  const text = protectStringLiterals(textBeforeCaret);

  // walk the open calls: a stack of { name, args }
  const stack = [];
  const nameRegex = /([A-Za-z][A-Za-z0-9_]*)\s*$/;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === "(") {
      const before = nameRegex.exec(text.slice(0, i));
      stack.push({ name: before ? before[1] : null, args: 0 });
    } else if (c === ")") {
      stack.pop();
    } else if (c === "," && stack.length) {
      stack[stack.length - 1].args++;
    }
  }

  for (let i = stack.length - 1; i >= 0; i--) {
    const open = stack[i];
    if (!open.name) continue;
    const fn = registry.find(f => f.name === open.name.toUpperCase() && f.name === open.name);
    if (fn) return { fn, paramIndex: open.args };
  }
  return null;
};
